import React, { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Card, Button, Modal, Row, Col } from "react-bootstrap";
import { FaTelegram, FaShoppingCart, FaCheckCircle } from "react-icons/fa";
import { BsCartX } from "react-icons/bs";
import Logo from "../assets/images/New_Logo.png";
import Swal from 'sweetalert2';
import axiosInstance from "../utils/axiosConfig";
import { AuthContext } from "../context/AuthContext";
import { CartContext } from "../context/CartContext";
import { useTranslation } from "react-i18next";

const Payment = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const savedData = JSON.parse(localStorage.getItem("paymentData") || "{}");

  const formData = location.state?.formData || savedData.formData || {};
  const cart = location.state?.cart || savedData.cart || [];
  const total = location.state?.total || savedData.total || 0;
  const [showModal, setShowModal] = useState(false);
  const [finalTotal, setFinalTotal] = useState(0);

  const [orderCreated, setOrderCreated] = useState(false);
  const { user } = useContext(AuthContext);
  const { clearCart } = useContext(CartContext);
  // eslint-disable-next-line no-unused-vars
  const _total = Number(total) || 0;
  const [telegramOpened, setTelegramOpened] = useState(false);
  const [pageBlurred, setPageBlurred] = useState(false);
  const [recoveryShown, setRecoveryShown] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  // Mobile detection
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);

    // Additional scroll after a tiny delay to ensure everything is rendered
    const timer = setTimeout(() => {
      // Get the form or cart container
      const formContainer = document.querySelector('.delivery-form') ||
        document.querySelector('form') ||
        document.querySelector('.mt-top-cartpage') ||
        document.querySelector('.cart-items-sidebar');

      if (formContainer) {
        formContainer.scrollTop = 0;

        // Also focus on first input if it's the checkout form
        if (window.location.pathname.includes('checkout')) {
          const firstInput = document.querySelector('input, select, textarea');
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
          }
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Event listeners for mobile/desktop
  useEffect(() => {
    const handlePageBlur = () => {
      console.log('Page blurred (user leaving)');
      setPageBlurred(true);
    };

    const handlePageFocus = () => {
      console.log('Page focused (user returning)');
      // On mobile, check immediately when focus returns
      if (pageBlurred && telegramOpened && !orderCreated && !recoveryShown) {
        // Small delay to ensure page is ready
        setTimeout(() => {
          checkAndRecoverOrder();
        }, 500);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible');
        // Similar logic for visibility change
        if (pageBlurred && telegramOpened && !orderCreated && !recoveryShown) {
          setTimeout(() => {
            checkAndRecoverOrder();
          }, 500);
        }
      }
    };

    window.addEventListener('blur', handlePageBlur);
    window.addEventListener('focus', handlePageFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handlePageBlur);
      window.removeEventListener('focus', handlePageFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageBlurred, telegramOpened, orderCreated, recoveryShown]);

  // Check for pending orders on page load
  useEffect(() => {
    const checkOnLoad = () => {
      // Check if there's a pending order that needs recovery
      const hasPendingOrder = localStorage.getItem("telegramMessage") &&
        localStorage.getItem("lastOrderId");

      if (hasPendingOrder && !orderCreated && !recoveryShown) {
        console.log('Found pending order on page load');
        // Wait a bit for page to fully load
        setTimeout(() => {
          checkAndRecoverOrder();
        }, 1000);
      }
    };

    checkOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCreated, recoveryShown]);

  // Add state for delivery limit
  const [deliveryLimit, setDeliveryLimit] = useState(45); // Default to 45 if API fails

  const translateProvince = (provinceCode) => {
    const provinceMap = {
      "banteay_meanchey": "បន្ទាយមានជ័យ",
      "battambang": "បាត់ដំបង",
      "kampong_cham": "កំពង់ចាម",
      "kampong_chhnang": "កំពង់ឆ្នាំង",
      "kampong_speu": "កំពង់ស្ពឺ",
      "kampong_thom": "កំពង់ធំ",
      "kampot": "កំពត",
      "kandal": "កណ្ដាល",
      "koh_kong": "កោះកុង",
      "kratie": "ក្រចេះ",
      "mondulkiri": "មណ្ឌលគិរី",
      "phnom_penh": "ភ្នំពេញ",
      "preah_vihear": "ព្រះវិហារ",
      "prey_veng": "ព្រៃវែង",
      "pursat": "ពោធិ៍សាត់",
      "siem_reap": "សៀមរាប",
      "sihanoukville": "ព្រះសីហនុ",
      "stung_treng": "ស្ទឹងត្រែង",
      "svay_rieng": "ស្វាយរៀង",
      "takeo": "តាកែវ",
      "oddar_meanchey": "ឧត្ដរមានជ័យ",
      "ratanakiri": "រតនគិរី",
      "tboung_khmum": "ត្បូងឃ្មុំ"
    };

    return provinceMap[provinceCode] || provinceCode;
  };

  const translateDeliveryMethod = (method) => {
    const deliveryMethodMap = {
      "standard": "Cash on Delivery",
      "vtb": "Vireak Buntham",
      "j&t": "J&T Express"
    };

    return deliveryMethodMap[method] || method;
  };

  useEffect(() => {
    const calculateFinalTotal = () => {
      // Calculate subtotal
      const subtotal = cart.reduce(
        (sum, item) =>
          sum +
          (item.product.discount > 0
            ? item.product.price * (1 - item.product.discount / 100)
            : item.product.price) *
          item.quantity,
        0
      );

      // Round to 2 decimal places
      const roundedSubtotal = Math.round(subtotal * 100) / 100;

      // Get delivery fee
      const deliveryFee = getDeliveryFee();
      const isFree = deliveryFee === "free";
      const feeValue = isFree ? 0 : Number(deliveryFee);

      console.log("Calculation details:", {
        subtotal: roundedSubtotal,
        deliveryFee,
        feeValue,
        deliveryLimit,
        isFreeDelivery: roundedSubtotal >= deliveryLimit
      });

      const newTotal = roundedSubtotal + feeValue;
      setFinalTotal(newTotal);

      localStorage.setItem(
        "paymentData",
        JSON.stringify({ formData, cart, total: newTotal })
      );
    };

    calculateFinalTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, formData, deliveryLimit]);

  // Fetch delivery limit from backend
  useEffect(() => {
    const fetchDeliveryLimit = async () => {
      try {
        const response = await axiosInstance.get("/delivery-free-limits");
        const limits = response.data;
        if (Array.isArray(limits) && limits.length > 0) {
          setDeliveryLimit(limits[0].amount);
        }
      } catch (error) {
        console.error("Failed to fetch delivery limit:", error);
        // Keep default value of 45
      }
    };

    fetchDeliveryLimit();
  }, []);

  useEffect(() => {
    const checkOrderCreated = () => {
      try {
        const existing = localStorage.getItem("orderCreated") === "true";
        if (existing) {
          setOrderCreated(true);
        }
      } catch (err) {
        console.error("Error checking orderCreated flag:", err);
      }
    };

    checkOrderCreated();

    const onVisibility = () => {
      if (!document.hidden) checkOrderCreated();
    };
    const onFocus = () => checkOrderCreated();
    const onPageShow = (e) => {
      checkOrderCreated();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // In Payment.js, update the createSimplifiedTelegramMessage function
  // In Payment.js, update the createSimplifiedTelegramMessage function
  const createSimplifiedTelegramMessage = (formData, cart, total, orderId) => {
    const currentDateTime = new Date().toLocaleString("en-US", { timeZone: "Asia/Phnom_Penh" });
    const deliveryFee = getDeliveryFee();
    const isFreeDelivery = deliveryFee === "free";
    const deliveryFeeDisplay = isFreeDelivery ? "Free" : "$" + Number(deliveryFee).toFixed(2);

    const translatedProvince = translateProvince(formData.province);
    const translatedDeliveryMethod = translateDeliveryMethod(formData.delivery);

    const productsList = cart.map((item, idx) => {
      const product = item.product;
      const originalPrice = product?.price || 0;
      const discount = product?.discount || 0;
      const finalPrice = discount > 0
        ? originalPrice * (1 - discount / 100)
        : originalPrice;
      const discountText = discount > 0 ? `${discount}% off` : "0 %";
      const totalItemPrice = finalPrice * item.quantity;

      const numberEmoji = `${idx + 1}️⃣`;

      // Build product title with capacity AND color information
      let productTitle = product?.title || 'Unknown Product';
      const details = [];

      // Add capacity (ML) if available
      if (product?.selectedCapacity?.name) {
        details.push(product.selectedCapacity.name);
      }

      // Add color if available
      if (product?.selectedColor?.name) {
        details.push(product.selectedColor.name);
      }

      // Add details to title
      if (details.length > 0) {
        productTitle = `${productTitle} (${details.join(' - ')})`;
      }

      // Also check if title already contains info from displayTitle
      if (product?.displayTitle && !productTitle.includes(product.displayTitle)) {
        productTitle = product.displayTitle;
      }

      if (item.quantity > 1) {
        return `${numberEmoji} ${productTitle} ( ×${item.quantity} ) : $${originalPrice} (${discountText}) → $${finalPrice.toFixed(2)} → $${totalItemPrice.toFixed(2)}`;
      } else {
        return `${numberEmoji} ${productTitle} ( ×${item.quantity} ) : $${originalPrice} (${discountText}) → $${finalPrice.toFixed(2)}`;
      }
    }).join('\n\n');

    return `🛍 New Order Details 🛍

📦 Order ID: #${orderId}
📅 Date: ${currentDateTime}
-------------------------------------
              Customer Information
-------------------------------------

👤 Customer: ${formData.name}

📞 Phone: ${formData.phone}

🏠 Province: ${translatedProvince}

🛣 Address: ${formData.address}

🚚 Delivery Method: ${translatedDeliveryMethod}

-------------------------------------
              🛒 Product List
-------------------------------------

${productsList}

-----------------------------------

🚚 Delivery Fee: ${deliveryFeeDisplay}
💵 Total Amount: $${Number(total).toFixed(2)}

📈 Please scan this our QR code to pay and upload your transaction to this chat.

🔗 [ABA Pay QR](https://res.cloudinary.com/dwtfz4jav/image/upload/v1763774964/photo_2025-11-22_08-29-06_iqgiuu.jpg)

`;
  };

  // Recovery function
  const checkAndRecoverOrder = async () => {
    // Prevent multiple dialogs
    if (recoveryShown) return;

    const orderId = localStorage.getItem("lastOrderId");
    const telegramMessage = localStorage.getItem("telegramMessage");

    console.log('checkAndRecoverOrder called:', {
      orderId: !!orderId,
      telegramMessage: !!telegramMessage,
      orderCreated,
      recoveryShown
    });

    if (orderId && telegramMessage && !orderCreated) {
      setRecoveryShown(true);

      console.log('Showing recovery dialog for order:', orderId);

      // Use a simple delay to ensure Swal can render properly
      await new Promise(resolve => setTimeout(resolve, 300));

      const { value: shouldResend } = await Swal.fire({
        title: t("resend_order_title", { orderId }),
        text: t("resend_order_text"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("resend_yes"),
        cancelButtonText: t("resend_no"),
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: true
      });

      if (shouldResend) {
        console.log('User chose to resend');
        const telegramUrl = `https://t.me/Pteas_Pet_Tmenh?text=${encodeURIComponent(telegramMessage)}`;

        // Open Telegram
        if (isMobileDevice()) {
          // For mobile, use window.location
          window.location.href = telegramUrl;
        } else {
          // For desktop, use window.open
          window.open(telegramUrl, '_blank');
        }

        // Mark as sent
        localStorage.setItem("orderCreated", "true");
        setOrderCreated(true);

        // Clear pending data after sending
        localStorage.removeItem("telegramMessage");
        localStorage.removeItem("telegramOrderId");
        localStorage.removeItem("lastOrderId");
        localStorage.removeItem("lastOrderData");
      } else {
        console.log('User chose not to resend');
        // Clear the pending order
        localStorage.removeItem("lastOrderId");
        localStorage.removeItem("telegramMessage");
        localStorage.removeItem("lastOrderData");
        localStorage.removeItem("telegramOrderId");
        setRecoveryShown(false);
      }
    }
  };

  // Create order function
  const createOrder = async () => {
    if (isCreatingOrder) return;
    setIsCreatingOrder(true);

    // ⏳ Delay execution (UX / anti-double-click)
    setTimeout(async () => {
      try {
        // 1️⃣ Validate input data
        if (!formData || !cart || cart.length === 0) {
          throw new Error("Missing order information");
        }

        // 2️⃣ Check authentication
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        // 3️⃣ Calculate subtotal
        const subtotal = cart.reduce(
          (sum, item) =>
            sum +
            (item.product.discount > 0
              ? item.product.price * (1 - item.product.discount / 100)
              : item.product.price) *
            item.quantity,
          0
        );

        const roundedSubtotal = Math.round(subtotal * 100) / 100;
        const deliveryFee = getDeliveryFee();
        const isFreeDelivery = deliveryFee === "free";
        const deliveryFeeValue = isFreeDelivery ? 0 : Number(deliveryFee);
        const finalTotalAmount = roundedSubtotal + deliveryFeeValue;

        // 4️⃣ Prepare order payload
        const orderData = {
          customerId: user?._id || "GUEST",
          customerName: formData.name,
          customerPhone: formData.phone,
          province: formData.province,
          address: formData.address,
          deliveryMethod: formData.delivery,
          products: cart.map(item => ({
            productId: item.product._id,
            productName: item.product.title,
            quantity: item.quantity,
            unitPrice:
              item.product.discount > 0
                ? item.product.price * (1 - item.product.discount / 100)
                : item.product.price,
            discount: item.product.discount || 0,
            totalPrice:
              item.quantity *
              (item.product.discount > 0
                ? item.product.price * (1 - item.product.discount / 100)
                : item.product.price),
            // ✅ Preserve selected image
            productImage:
              item.product.image ||
              item.product.images?.[0] ||
              item.product.productImage ||
              "",
            selectedColor: item.product.selectedColor || null,
            selectedCapacity: item.product.selectedCapacity || null
          })),
          totalAmount: finalTotalAmount,
          deliveryFee: deliveryFeeValue,
          isFreeDelivery,
          status: "pending"
        };

        // 5️⃣ Create order
        const orderResponse = await axiosInstance.post(
          "/orders/create",
          orderData
        );

        if (!orderResponse.data?.success) {
          throw new Error(
            orderResponse.data?.message || "Order creation failed"
          );
        }

        const orderId = orderResponse.data.order.orderId;

        // 6️⃣ Persist recovery data
        localStorage.setItem("lastOrderId", orderId);
        localStorage.setItem(
          "lastOrderData",
          JSON.stringify({
            formData,
            cart,
            total: finalTotalAmount,
            timestamp: Date.now()
          })
        );

        // 7️⃣ Clear cart AFTER success
        await clearCart();

        localStorage.setItem("orderCreated", "true");
        setOrderCreated(true);

        // 8️⃣ Telegram message
        const telegramMessage = createSimplifiedTelegramMessage(
          formData,
          cart,
          finalTotalAmount,
          orderId
        );

        localStorage.setItem("telegramMessage", telegramMessage);
        localStorage.setItem("telegramOrderId", orderId);

        // 9️⃣ Close modal
        setShowModal(false);

        // Recovery flags
        setTelegramOpened(true);
        setPageBlurred(false);
        setRecoveryShown(false);

        const telegramUrl = `https://t.me/Pteas_Pet_Tmenh?text=${encodeURIComponent(
          telegramMessage
        )}`;

        // Simulate blur
        setTimeout(() => setPageBlurred(true), 100);

        // 🔟 Open Telegram
        setTimeout(() => {
          if (isMobileDevice()) {
            window.location.href = telegramUrl;
          } else {
            window.open(telegramUrl, "_blank");
          }
        }, 300);

        // 1️⃣1️⃣ Fallback recovery
        setTimeout(() => {
          if (!pageBlurred && !orderCreated) {
            checkAndRecoverOrder();
          }
        }, 3000);

      } catch (error) {
        console.error("Order Creation Error:", error);

        let errorMessage = "An unexpected error occurred";
        if (error.message?.toLowerCase().includes("network")) {
          errorMessage = "Network error. Please check your internet connection.";
        } else if (error.message?.toLowerCase().includes("token")) {
          errorMessage = "Session expired. Please login again.";
        } else {
          errorMessage = error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonText: "Try Again"
        }).then(result => {
          if (result.isConfirmed) {
            createOrder();
          }
        });
      } finally {
        setIsCreatingOrder(false);
      }
    }, 2500); // ⏱ 5s delay
  };

  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (!orderCreated) {
        localStorage.removeItem("orderCreated");
      }
    };
  }, [orderCreated]);

  const getDeliveryFee = () => {
    const subtotal = cart.reduce(
      (total, cartItem) =>
        total + (
          cartItem.product.discount > 0
            ? cartItem.product.price * (1 - cartItem.product.discount / 100)
            : cartItem.product.price
        ) * cartItem.quantity,
      0
    );

    // Round to 2 decimal places to avoid floating-point precision issues
    const roundedSubtotal = Math.round(subtotal * 100) / 100;

    console.log("getDeliveryFee - subtotal:", roundedSubtotal, "limit:", deliveryLimit);

    // Use dynamic delivery limit from backend
    if (roundedSubtotal >= deliveryLimit) return "free";

    if (!formData.province || !formData.delivery) return 0;

    if (formData.province === "phnom_penh") {
      if (formData.delivery === "standard") return 1.5;
      return 2.0;
    }

    return 2.0;
  };

  const handleDoneClick = () => {
    setShowModal(true);
  };

  const handleContinueShopping = () => {
    // Clear ALL order-related data
    localStorage.removeItem("orderCreated");
    localStorage.removeItem("paymentData");
    localStorage.removeItem("lastOrderId");
    localStorage.removeItem("lastOrderData");
    localStorage.removeItem("telegramMessage");
    localStorage.removeItem("telegramOrderId");

    // Also clear the cart context
    clearCart();

    // Reset state
    setOrderCreated(false);
    setTelegramOpened(false);
    setPageBlurred(false);
    setRecoveryShown(false);

    navigate("/order-history");
  };

  return (
    <main style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      {orderCreated ? (
        <section className="d-flex justify-content-center align-items-center clear-cart-section">
          <Container className="text-center">
            <BsCartX className="text-secondary" style={{ fontSize: "5rem", marginBottom: "1rem" }} />
            <h3>{t('Your cart is Clear')}</h3>
            <p className="text-muted"> {t('Your payment was successful, and your order has been placed.')} </p>
            <Button
              variant="primary"
              onClick={handleContinueShopping}
              className="mt-3 btn-color btn-card-hover border-0 py-2 px-3"
            >
              <FaShoppingCart className="me-2" />
              {t('Continue Shopping')}
            </Button>
          </Container>
        </section>
      ) : (
        <section className="d-flex justify-content-center align-items-center mt-custom-payment-modal">
          <Container fluid className="d-flex justify-content-center align-items-center full-height bg-light">
            <Row className="w-100 justify-content-center">
              <Col md={6} className="d-flex justify-content-center align-items-center full-height">
                <Card className="border-0 shadow-lg rounded-4 p-0 w-100"
                  style={{
                    maxWidth: "460px",
                    background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                  }}>
                  <Card.Header className="bg-transparent border-0 text-center pb-2 mt-3">
                    <img
                      src={Logo}
                      alt="Logo"
                      className="img-fluid rounded-circle"
                      style={{
                        width: "140px",
                        height: "140px",
                        objectFit: "contain",
                        boxShadow:
                          "0 0 16px rgba(0, 0, 0, 0.08), 0 0 8px rgba(0, 0, 0, 0.06)",
                      }} />
                  </Card.Header>
                  <Card.Body className="text-center mt-2 d-flex flex-column align-items-center py-4 px-3">
                    <p className="text-secondary fw-medium mb-2 fs-4">
                      {t("Amount to Pay")}
                    </p>
                    <h1 className="fw-bold text-danger mb-4 mt-4">
                      ${finalTotal.toFixed(2)}{" "}
                      <span className="text-muted fs-6">{t("USD")}</span>
                    </h1>
                  </Card.Body>
                  <Card.Footer className="border-0 p-0 mt-2">
                    <Button
                      className="w-100 py-3 fw-semibold text-white rounded-bottom-4 rounded-top-0"
                      style={{
                        background: "#000000",
                        border: "none",
                        letterSpacing: "0.5px",
                        transition: "0.3s ease",
                        fontSize: "16px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = "#ff0000";
                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(255, 0, 0, 0.4)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "#000000";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)";
                      }}
                      onClick={handleDoneClick}
                    >
                      {t("ARE YOU PAY DONE ?")}
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
        <Modal.Body className="text-center d-flex flex-column align-items-center">
          <FaCheckCircle className="text-success success-size-icon my-4 bg-transparent" />
          <h4>{t('Is your payment successful?')}</h4>
          <p className="mt-4"> {t('Click below to create order and sent your transaction into my Telegram Account')} </p>

          <Button
            variant="primary"
            onClick={createOrder}
            disabled={isCreatingOrder}
            className="my-3 btn-color btn-card-hover border-0 w-75 py-2 d-flex justify-content-center align-items-center"
          >
            {isCreatingOrder ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                {t("Processing...")}
              </>
            ) : (
              <>
                {t("Create Order")}
                <FaTelegram className="ms-2 fs-4 text-white" />
              </>
            )}
          </Button>

        </Modal.Body>
      </Modal>
    </main>
  );
};

export default Payment;

//Correct with 755 line code changes
